/// <amd-module name="VSSUI/Components/Autocomplete/Suggestions" />
import * as React from "react";
import * as Resources from "VSSUI/Resources.Label";

import { css, getSafeId } from "VSSUI/Util";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ISuggestionsProps } from "./Suggestions.Props";
import { Label } from "../Label/Label";
import { ILabelModel } from "../Label/Label.Props";
import { ColorPip, ColorSwatchPicker } from "VSSUI/Color";
import { IColor } from "VSSUI/Utilities/Color";
import { Icon } from "VSSUI/Icon";

interface ISuggestionState {
    isNewRowHovered?: boolean;
}

export class Suggestions extends React.Component<ISuggestionsProps, ISuggestionState> {
    private currentSelectedElementRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
    private previousSelectedElement: HTMLDivElement;

    constructor(props: ISuggestionsProps) {
        super(props);

        this.state = {
            isNewRowHovered: false
        };
    }

    public render() {
        const { isCurrentInputAlreadyInGroup, isLoading, onCheckForExactMatch, suggestedItems, swatchPickerColors } = this.props;

        const minWidth: number = swatchPickerColors.length * 32;

        // Determine items to render
        const calloutItems: JSX.Element[] = [];

        const renderLoadingRow: boolean = isLoading;
        const renderIncludedRow: boolean = isCurrentInputAlreadyInGroup;
        const renderNewLabelRow: boolean = !renderIncludedRow && (onCheckForExactMatch === undefined || !onCheckForExactMatch(suggestedItems));

        if (renderLoadingRow) {
            calloutItems.push(this.onRenderLoadingRow());
        }

        suggestedItems.length > 0 &&
            calloutItems.push(
                <div className="bolt-label-suggestions-list" key="suggestions">
                    {suggestedItems.map(this.onRenderSuggestionRow)}
                </div>
            );

        if (renderIncludedRow) {
            calloutItems.push(this.onRenderAlreadyIncludedRow());
        }

        if (renderNewLabelRow) {
            calloutItems.push(this.onRenderNewLabelRow(swatchPickerColors));
        }

        // Interleave separators as necessary
        const calloutContents: JSX.Element[] = [];
        calloutItems.forEach((element: JSX.Element, index: number) => {
            index !== 0 && calloutContents.push(<div className="bolt-suggestions-separator" key={"separator" + index} />);
            calloutContents.push(element);
        });

        return (
            <div
                className="bolt-label-suggestions-container flex-column"
                id={getSafeId("autocomplete-listbox")}
                role="listbox"
                style={{ minWidth: minWidth }}
            >
                {calloutContents}
            </div>
        );
    }

    public componentDidUpdate() {
        if (this.currentSelectedElementRef.current && this.currentSelectedElementRef.current !== this.previousSelectedElement) {
            this.currentSelectedElementRef.current.scrollIntoView(false);
            this.previousSelectedElement = this.currentSelectedElementRef.current;
        }
    }

    private isNewRowSelected(): boolean {
        return this.props.currentSelectedIndex === this.props.suggestedItems.length;
    }

    private onHoverNewRowEnd = () => {
        this.setState({
            isNewRowHovered: false
        });
    };

    private onHoverNewRowStart = () => {
        this.setState({
            isNewRowHovered: true
        });
    };

    private onRenderAlreadyIncludedRow = () => {
        const { inputAlreadyInGroupText = Resources.LabelInGroup } = this.props;

        return (
            <div className="bolt-label-suggestions-row flex-row" key="included">
                <Icon className="bolt-label-suggestions-icon" iconName="CheckMark" />
                <div className="bolt-label-suggestions-row--content">{inputAlreadyInGroupText}</div>
            </div>
        );
    };

    private onRenderLoadingRow = () => {
        return (
            <div className="bolt-label-suggestions-loading-row" key="loading">
                <Spinner size={SpinnerSize.small} label={Resources.LoadingSuggestions} />
            </div>
        );
    };

    private onRenderNewLabelRow = (colors: IColor[]) => {
        let content: JSX.Element;

        if (this.shouldDisplayColorPicker()) {
            content = (
                <ColorSwatchPicker
                    className={css(this.isNewRowSelected() && "selected")}
                    colors={colors}
                    onPipClick={this.props.onColorPipClick}
                    selectedIndex={this.props.currentSelectedColorIndex}
                />
            );
        } else {
            content = (
                <div
                    className={css("bolt-label-suggestions-row clickable flex-row", this.isNewRowSelected() && "selected")}
                    onClick={this.props.onNewLabelClick}
                    role="option"
                    tabIndex={-1}
                >
                    <ColorPip color={this.props.disableColorPicker && Label.DEFAULT_COLOR} />
                    <div className="bolt-label-suggestions-row--content">{Resources.NewLabelSuggestionText}</div>
                </div>
            );
        }

        return (
            <div
                className="bolt-label-suggestions-newRow-wrapper"
                key="newRow"
                onMouseEnter={this.onHoverNewRowStart}
                onMouseLeave={this.onHoverNewRowEnd}
            >
                {content}
            </div>
        );
    };

    private onRenderSuggestionRow = (labelModel: ILabelModel, index: number) => {
        const isSelected: boolean = this.props.currentSelectedIndex === index;

        return (
            <div
                aria-selected={isSelected}
                className={css("bolt-label-suggestions-row clickable flex-row", isSelected && "selected")}
                id={getSafeId(labelModel.content)}
                key={labelModel.content}
                onClick={(event: React.MouseEvent<HTMLDivElement>) => this.props.onSuggestionClick(event, labelModel)}
                ref={isSelected && this.currentSelectedElementRef}
                role="option"
                tabIndex={-1}
            >
                <ColorPip color={labelModel.color} />
                <div className="bolt-label-suggestions-row--content">
                    <span>{labelModel.content}</span>
                </div>
            </div>
        );
    };

    private shouldDisplayColorPicker(): boolean {
        const { disableColorPicker = false } = this.props;
        return !disableColorPicker && (this.isNewRowSelected() || this.state.isNewRowHovered);
    }
}
